import { z } from 'zod';
export const trainingReportingTeamSummaryCapability = 'training.reporting-team-summary.v1';
export const trainingReportingTeamSummarySlot = 'reporting.training-team-summary';
export const trainingReportingTeamSummaryProviderId = 'training.server.reporting-team-summary';
const countSchema = z.number().int().nonnegative().safe();
const nullableTimestampSchema = z.string().datetime({ offset: true }).nullable();
export const trainingReportingTeamSummaryRequestSchema = z.strictObject({
    consumerModuleId: z.literal('reporting'),
    requestedCapability: z.literal('reporting.training-team-summary.v1'),
    resourceType: z.literal('reporting.dataset'),
    resourceId: z.string().uuid(),
    membershipIds: z.array(z.string().uuid()).min(1).max(500),
    asOf: z.string().datetime({ offset: true }),
}).superRefine(({ membershipIds }, context) => {
    if (new Set(membershipIds).size !== membershipIds.length) {
        context.addIssue({
            code: 'custom', path: ['membershipIds'], message: 'Memberships cannot repeat.',
        });
    }
});
export const trainingReportingTeamSummaryFactSchema = z.strictObject({
    membershipId: z.string().uuid(),
    assignmentTotal: countSchema,
    assignmentOpen: countSchema,
    assignmentCompleted: countSchema,
    assignmentWaived: countSchema,
    assignmentOverdue: countSchema,
    completedCourseCount: countSchema,
    assessmentAttemptCount: countSchema,
    assessmentPassedCount: countSchema,
    averageScoreBasisPoints: z.number().int().min(0).max(10_000).nullable(),
    latestAssessmentSubmittedAt: nullableTimestampSchema,
    activitySessionCount: countSchema,
    activityActiveSeconds: countSchema,
    latestActivityAt: nullableTimestampSchema,
}).superRefine((fact, context) => {
    if (fact.assignmentTotal !==
        fact.assignmentOpen + fact.assignmentCompleted + fact.assignmentWaived) {
        context.addIssue({
            code: 'custom', path: ['assignmentTotal'],
            message: 'Assignment classifications must reconcile exactly.',
        });
    }
    if (fact.assignmentOverdue > fact.assignmentOpen) {
        context.addIssue({
            code: 'custom', path: ['assignmentOverdue'],
            message: 'Overdue assignments must be open assignments.',
        });
    }
    if (fact.assessmentPassedCount > fact.assessmentAttemptCount) {
        context.addIssue({
            code: 'custom', path: ['assessmentPassedCount'],
            message: 'Passed attempts cannot exceed submitted attempts.',
        });
    }
    const hasAssessments = fact.assessmentAttemptCount > 0;
    if (hasAssessments !== (fact.averageScoreBasisPoints !== null) ||
        hasAssessments !== (fact.latestAssessmentSubmittedAt !== null)) {
        context.addIssue({
            code: 'custom', path: ['assessmentAttemptCount'],
            message: 'Assessment aggregates must preserve explicit missing state.',
        });
    }
    const hasActivity = fact.activitySessionCount > 0;
    if (hasActivity !== (fact.latestActivityAt !== null)) {
        context.addIssue({
            code: 'custom', path: ['activitySessionCount'],
            message: 'Activity aggregates must preserve explicit missing state.',
        });
    }
});
export const trainingReportingTeamSummaryResponseSchema = z.strictObject({
    state: z.literal('present'),
    capability: z.literal(trainingReportingTeamSummaryCapability),
    tenantId: z.string().uuid(),
    asOf: z.string().datetime({ offset: true }),
    activityBasis: z.literal('supporting-training-activity'),
    payableTime: z.literal(false),
    memberships: z.array(trainingReportingTeamSummaryFactSchema).min(1).max(500),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
}).superRefine(({ memberships }, context) => {
    const ids = memberships.map(({ membershipId }) => membershipId);
    if (new Set(ids).size !== ids.length) {
        context.addIssue({ code: 'custom', path: ['memberships'], message: 'Memberships cannot repeat.' });
    }
});
//# sourceMappingURL=training-reporting-team-summary.contract.js.map